function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Text inputs match :focus-visible on tap/click too, so inputs styled with outline: 0 show their focus ring
// only while the shopper is navigating with the keyboard (data-keyboard-nav on <html>, see custom.css)
window.addEventListener('keydown', (event) => {
  if (event.key === 'Tab') document.documentElement.dataset.keyboardNav = '';
});
window.addEventListener('pointerdown', () => delete document.documentElement.dataset.keyboardNav);

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

// Tabbed sections and the quiz steps render hidden sliders with init="false"; build each one the first time it is shown
function buildTabSwiper(container) {
  if (container.getAttribute('init') === 'false' && !container.swiper) container.initialize();
}

class NewArticlesList extends HTMLElement {
  constructor(){
    super();
    this.tabs = ['.new-articles-list-tab-item', '.new-articles-list-tab-more-item'];
    this.tabContent = this.querySelectorAll('.new-articles-list-content-swiper');
    this.mobileDropdownBtn = this.querySelector('.new-articles-list-tab-more-title');
    this.mobileDropdown = this.querySelector('.new-articles-list-tab-more');
    this.titles = this.querySelectorAll('.new-articles-list-content-title');
    // this.swiper = this.querySelector('.new-articles-list-content-swiper');
    this.init();
  }

  init(){
    if(this.tabs){
    this.tabs.forEach(tabItem => {
      this.querySelectorAll(tabItem).forEach(tab => {
        tab.addEventListener("click", e => {

          this.tabs.forEach(tabItem => {
            this.querySelectorAll(tabItem).forEach(tab => tab.classList.remove('active'))
          })

          this.tabContent.forEach(tabContent => tabContent.classList.remove('active'))

          let tabTitle = e.currentTarget.getAttribute('data-title')
          e.currentTarget.classList.add('active')

          this.tabContent.forEach(tabContent => {
            let tabContentTitle = tabContent.getAttribute('data-title');
            if(tabTitle == tabContentTitle){
              tabContent.classList.add('active')
              buildTabSwiper(tabContent)
            }
          })
        })
      })
    })
    }

    if(this.mobileDropdownBtn){
    this.mobileDropdownBtn.addEventListener('click', e => {
      e.stopPropagation();
      e.currentTarget.classList.add('active');
      this.mobileDropdown.classList.add('active')
    })
    }

    document.addEventListener('click', e => {
      if(this.mobileDropdown && this.mobileDropdownBtn){
      this.mobileDropdown.classList.remove('active')
      this.mobileDropdownBtn.classList.remove('active');
      }
    })

    if(this.titles.length > 0){
      this.titles.forEach(el => this.toggleTruncatedClass(el));
    }
  }

  toggleTruncatedClass(el) {
    let isOverflowing = el.scrollWidth > el.clientWidth;
    el.closest('div').classList.toggle('is-truncated', isOverflowing);
  }

}
customElements.define('new-articles-list', NewArticlesList);

class NewCollectionsList extends HTMLElement {
  constructor(){
    super();
    this.tabs = ['.new-collections-list-tab-item', '.new-collections-list-tab-more-item'];
    this.tabContent = this.querySelectorAll('.new-collections-list-content-swiper');
    this.mobileDropdownBtn = this.querySelector('.new-collections-list-tab-more-title');
    this.mobileDropdown = this.querySelector('.new-collections-list-tab-more');
    this.init();
  }

  init(){
    if(this.tabs){
    this.tabs.forEach(tabItem => {
      this.querySelectorAll(tabItem).forEach(tab => {
        tab.addEventListener("click", e => {

          this.tabs.forEach(tabItem => {
            this.querySelectorAll(tabItem).forEach(tab => tab.classList.remove('active'))
          })

          this.tabContent.forEach(tabContent => tabContent.classList.remove('active'))

          let tabTitle = e.currentTarget.getAttribute('data-title')
          e.currentTarget.classList.add('active')

          this.tabContent.forEach(tabContent => {
            let tabContentTitle = tabContent.getAttribute('data-title');
            if(tabTitle == tabContentTitle){
              tabContent.classList.add('active')
              buildTabSwiper(tabContent)
            }
          })
        })
      })
    })
    }

    if(this.mobileDropdownBtn){
    this.mobileDropdownBtn.addEventListener('click', e => {
      e.stopPropagation();
      e.currentTarget.classList.add('active');
      this.mobileDropdown.classList.add('active')
    })
    }

    document.addEventListener('click', e => {
      if(this.mobileDropdown && this.mobileDropdownBtn){
      this.mobileDropdown.classList.remove('active')
      this.mobileDropdownBtn.classList.remove('active');
      }
    })
  }
}
customElements.define('new-collections-list', NewCollectionsList);

class NewVideoSlider extends HTMLElement {
  constructor(){
    super();
    this.video = this.querySelectorAll('.new-video-slider-video');
    this.init();
  }

  init(){
    if(this.video){
    this.video.forEach(vid => {
        vid.addEventListener("click", e => {
          if(e.currentTarget.classList.contains('active')){
            e.currentTarget.classList.remove('active')
            e.currentTarget.pause();
          }
          else{
            this.video.forEach(vid => {
              vid.classList.remove('active')
              vid.pause();
            })
            e.currentTarget.classList.add('active')
            e.currentTarget.play();
          }
      })
    })
    }
  }
}
customElements.define('new-video-slider', NewVideoSlider);

document.querySelectorAll('.new-footer-acc').forEach((acc) => {
  acc.addEventListener('click', (e) => {
    e.currentTarget.classList.toggle('active');
  })
})

class NewProductsList extends HTMLElement {
  constructor(){
    super();
    this.tabs = ['.new-products-list-tab-item'];
    this.tabContent = this.querySelectorAll('.new-products-list-content-swiper');
    this.init();
  }

  init(){
    if(this.tabs){
    this.tabs.forEach(tabItem => {
      this.querySelectorAll(tabItem).forEach(tab => {
        tab.addEventListener("click", e => {

          this.tabs.forEach(tabItem => {
            this.querySelectorAll(tabItem).forEach(tab => tab.classList.remove('active'))
          })

          this.tabContent.forEach(tabContent => tabContent.classList.remove('active'))

          let tabTitle = e.currentTarget.getAttribute('data-title')
          e.currentTarget.classList.add('active')

          this.tabContent.forEach(tabContent => {
            let tabContentTitle = tabContent.getAttribute('data-title');
            if(tabTitle == tabContentTitle){
              tabContent.classList.add('active')
              buildTabSwiper(tabContent)
            }
          })
        })
      })
    })
    }
  }
}
customElements.define('new-products-list', NewProductsList);

class NewImageProductsList extends HTMLElement {
  constructor(){
    super();
    this.tabs = ['.new-iw-products-list-tab-item'];
    this.tabContent = this.querySelectorAll('.new-iw-products-list-content-swiper');
    this.init();
  }

  init(){
    if(this.tabs){
    this.tabs.forEach(tabItem => {
      this.querySelectorAll(tabItem).forEach(tab => {
        tab.addEventListener("click", e => {

          this.tabs.forEach(tabItem => {
            this.querySelectorAll(tabItem).forEach(tab => tab.classList.remove('active'))
          })

          this.tabContent.forEach(tabContent => tabContent.classList.remove('active'))

          let tabTitle = e.currentTarget.getAttribute('data-title')
          e.currentTarget.classList.add('active')

          this.tabContent.forEach(tabContent => {
            let tabContentTitle = tabContent.getAttribute('data-title');
            if(tabTitle == tabContentTitle){
              tabContent.classList.add('active')
              buildTabSwiper(tabContent)
            }
          })
        })
      })
    })
    }
  }
}
customElements.define('new-image-products-list', NewImageProductsList);

const CACHE_DB_NAME = 'pet_quiz_cache';
const CACHE_DB_VERSION = 1;
const CACHE_STORE_NAME = 'pet_data';
const CACHE_TTL_MS = 60 * 60 * 1000 * 24;
class NewQuiz extends HTMLElement {
  constructor(){
    super();
    this.options = this.querySelectorAll('.new-quiz-option-input');
    this.result = this.querySelector('.new-quiz-result-wrapper');
    this.prevBtn = this.querySelector('.new-quiz-prev');
    this.startOver = this.querySelector('.new-quiz-start');
    this.headerLeftProgress = this.querySelector('.new-quiz-header-left-progress');
    this.headerLeftResult = this.querySelector('.new-quiz-header-left-result');
    this.swiperWrapper = this.querySelector('.new-quiz-result-swiper');
    this.swiper = this.querySelector('.new-quiz-result-swiper').swiper;
    this.formWrapper = this.querySelector('.new-quiz-form-wrapper');
    this.form = this.querySelector('.new-quiz-form');
    this.finalResultWrapper = this.querySelector(".new-quiz-final-result-wrapper");
    this.notFoundWrapper = this.querySelector(".new-quiz-not-found");
    this.init();
  }

  init(){
    this.options.forEach(option => {
      option.addEventListener('change', e => {
        this.result.classList.remove('active');
        this.prevBtn.classList.remove('inactive');
        let currentStep = this.querySelector('.new-quiz-type.active');
        let currentQuestion = this.querySelector('.new-quiz-question-title.active');
        let currentAnswer = this.querySelector('.new-quiz-option-swiper.active');

        this.next(currentStep, currentQuestion, currentAnswer);
      })
    })

    this.prevBtn.addEventListener('click', (e) => {
      let currentStep = this.querySelector('.new-quiz-type.active');
      let currentQuestion = this.querySelector('.new-quiz-question-title.active');
      let currentAnswer = this.querySelector('.new-quiz-option-swiper.active');

      this.prev(currentStep, currentQuestion, currentAnswer);
    })

    this.startOver.addEventListener('click', (e) => {
      this.reset();
    })

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.submitToKlaviyo();
    });
  }

  showKlaviyoMessage(text) {
    const msg = this.querySelector('.new-quiz-klaviyo-msg');
    if (!msg) return;
    clearTimeout(this.klaviyoMsgTimeout);
    msg.textContent = text;
    msg.classList.add('active');
    this.klaviyoMsgTimeout = setTimeout(() => {
      msg.classList.remove('active');
    }, 3000);
  }
 
  klaviyoRequest(endpoint, body) {
    const companyId = this.getAttribute('company-id');
    return fetch(
      `https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify(body),
      }
    ).then((response) => {
      if (response.status === 202) return;
      return response
        .json()
        .catch(() => ({}))
        .then((error) => {
          throw new Error(error?.errors?.[0]?.detail || `Request failed (${response.status})`);
        });
    });
  }
 
  submitToKlaviyo() {
    if (this.form.querySelector(".new-quiz-form-btn")) this.form.querySelector(".new-quiz-form-btn").classList.add('active');
    let $this = this;
    const companyId = this.getAttribute('company-id');
    const listId = this.getAttribute('list-id');
    if (!companyId || !listId) {
      console.error('<new-quiz> needs company-id and list-id attributes');
      return;
    }
    const successMessage = this.getAttribute('success-message') || 'Thanks for subscribing!';
    const errorMessage = this.getAttribute('error-message') || 'Something went wrong, please try again.';
    const data = new FormData(this.form);
    const email = (data.get('email') || '').trim();
    const fullName = (data.get('name') || '').trim();
    const [firstName, ...rest] = fullName.split(/\s+/);
    const lastName = rest.join(' ');
    const phone = (data.get('Phone Number') || '').trim();
    const optedIn = data.get('newsletter') !== null;
    const attributes = {
      email,
      first_name: firstName,
      properties: {
        newsletter_opt_in: optedIn,
        signup_source: this.getAttribute('source') || 'Pet Quiz',
        phone
      },
    };
    if (lastName) attributes.last_name = lastName;
    const requests = [
      this.klaviyoRequest('profiles', { data: { type: 'profile', attributes } }),
    ];
    if (optedIn) {
      requests.push(
        this.klaviyoRequest('subscriptions', {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.getAttribute('source') || 'Pet Quiz',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        })
      );
    }
    Promise.all(requests)
    .then(() => {
      this.showKlaviyoMessage(successMessage);
      this.dispatchEvent(new CustomEvent('klaviyo:success', { bubbles: true, detail: { email } }));
      setTimeout(function(){
        $this.updateResult();
      }, 3000)
    })
    .catch((error) => {
      console.error('Klaviyo error:', error);
      this.showKlaviyoMessage(errorMessage);
      this.dispatchEvent(new CustomEvent('klaviyo:error', { bubbles: true, detail: { error } }));
    })
    .finally(() => {
      if (this.form.querySelector(".new-quiz-form-btn")) this.form.querySelector(".new-quiz-form-btn").classList.remove('active');
    });
  }


  next(currentStep, currentQuestion, currentAnswer) {
    let nextStep = currentStep.nextElementSibling;
    let nextQuestion = currentQuestion.nextElementSibling;
    let nextAnswer = currentAnswer.nextElementSibling;

    if(nextStep && nextStep.classList.contains('new-quiz-type')){
      currentStep.classList.add('complete');
      currentStep.classList.remove('active');
      nextStep.classList.add('active');

      this.scrollStepIntoView(nextStep);
    }

    if(nextQuestion && nextQuestion.classList.contains('new-quiz-question-title')){
      currentQuestion.classList.remove('active');
      nextQuestion.classList.add('active');
    }

    if(nextAnswer && nextAnswer.classList.contains('new-quiz-option-swiper')){
      currentAnswer.classList.remove('active');
      nextAnswer.classList.add('active');
      buildTabSwiper(nextAnswer);
    }
    else{
      this.result.classList.add('active');
      currentAnswer.classList.remove('active');
      currentQuestion.classList.remove('active');
      currentStep.classList.remove('active');
      currentStep.classList.add('complete');
      this.headerLeftProgress.classList.remove('active');
      this.headerLeftResult.classList.add('active');
    }
  }

  prev(currentStep, currentQuestion, currentAnswer) {
    let prevStep = currentStep.previousElementSibling;
    let prevQuestion = currentQuestion.previousElementSibling;
    let prevAnswer = currentAnswer.previousElementSibling;

    if(prevStep && prevStep.classList.contains('new-quiz-type')){
      currentStep.classList.remove('complete');
      currentStep.classList.remove('active');
      prevStep.classList.add('active');
      prevStep.classList.remove('complete');

      this.scrollStepIntoView(prevStep);
    }

    if(prevQuestion && prevQuestion.classList.contains('new-quiz-question-title')){
      currentQuestion.classList.remove('active');
      prevQuestion.classList.add('active');
    }

    if(prevAnswer && prevAnswer.classList.contains('new-quiz-option-swiper')){
      currentAnswer.classList.remove('active');
      prevAnswer.classList.add('active');
      if(!prevAnswer.previousElementSibling){
        this.prevBtn.classList.add('inactive');
      }
    }
  }

  reset() {
    this.options.forEach(option => {
      option.checked = false;
    });

    this.querySelectorAll('.new-quiz-type').forEach((item, index) => {
      item.classList.remove('active', 'complete');
      if (index === 0) {
        item.classList.add('active');
      }
    });

    this.querySelectorAll('.new-quiz-question-title').forEach((item, index) => {
      item.classList.remove('active');
      if (index === 0) {
        item.classList.add('active');
      }
    });

    this.querySelectorAll('.new-quiz-option-swiper').forEach((item, index) => {
      item.classList.remove('active');
      if (index === 0) {
        item.classList.add('active');
      }
    });

    this.result.classList.remove('active');
    this.headerLeftProgress.classList.add('active');
    this.headerLeftResult.classList.remove('active');
    this.prevBtn.classList.add('inactive');
    this.formWrapper.classList.add('active');
    this.swiperWrapper.classList.remove('active');
    this.querySelector('.new-quiz-type-wrapper').scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  }

  async updateResult(){
    this.swiperWrapper.innerHTML = '';
    this.formWrapper.classList.remove('active');
    this.finalResultWrapper.classList.add('active');
    this.notFoundWrapper.classList.remove("active");
    const petTypeInput = this.querySelector('.new-quiz-option-input[name="opt-pet-type"]:checked');
    let val = petTypeInput.value;
    // The answer's Metaobject Type Key (e.g. ncr_live_animal_pet_dog); no key means no pets to show
    const metaType = petTypeInput.dataset.metaType;
    let allPets = metaType ? await this.getAllPetsFromUrl(`${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(metaType)}&view=pets-json`, metaType) : [];
    let matchingPets = this.getMatchingPets(allPets);
    if(matchingPets.length > 0){
      let cards = ``;
      for(let item of matchingPets){
        cards += this.renderMetaCard(item);
      }
      this.finalResultWrapper.classList.remove('active');
      this.swiperWrapper.innerHTML = cards;
      this.swiperWrapper.classList.add('active');
      this.swiper.update();
    }
    else{
      this.finalResultWrapper.classList.remove('active');
      this.notFoundWrapper.classList.add("active");
    }
  }

  renderMetaCard(meta){
    return `<swiper-slide class="new-quiz-result-slide">
      <a href="${meta.url}" class="new-pet-card zoom-bg" style="--pet-image:
        linear-gradient(180deg, rgba(0, 0, 0, 0) 74.22%, rgba(33, 33, 33, 0.6) 100%),
        linear-gradient(0.24deg, rgba(0, 0, 0, 0.1) 0.31%, rgba(0, 0, 0, 0) 99.9%),
        url('${meta.image ? meta.image : window.petCardPlaceholder}');
      "> 
      <h2 class="new-pet-card-name">${escapeHTML(meta.name)}</h2>
        <div class="new-pet-card-desc">
          ${meta.dob != '' ? `<p class="new-pet-card-dob">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5 4.50391C13.5 5.33234 12.8284 6.00391 12 6.00391C11.1716 6.00391 10.5 5.33234 10.5 4.50391C10.5 3.67548 12 2.00391 12 2.00391C12 2.00391 13.5 3.67548 13.5 4.50391Z" stroke="currentColor" stroke-width="1.212" stroke-linejoin="round"></path>
              <path d="M12.002 6V9" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M17.6667 14C19.2315 14 20.5 12.8807 20.5 11.5C20.5 10.1193 19.2315 9 17.6667 9H6.33333C4.76853 9 3.5 10.1193 3.5 11.5C3.5 12.8807 4.76853 14 6.33333 14C7.70408 14 8.90415 13.1411 9.16667 12C9.42919 13.1411 10.6293 14 12 14C13.3707 14 14.5708 13.1411 14.8333 12C15.0959 13.1411 16.2959 14 17.6667 14Z" stroke="currentColor" stroke-width="1.212" stroke-linejoin="round"></path>
              <path d="M5 14.0039L5.52089 16.5835C6.04532 19.1807 6.30754 20.4793 7.19608 21.2416C8.08462 22.0039 9.33608 22.0039 11.839 22.0039H12.161C14.6639 22.0039 15.9154 22.0039 16.8039 21.2416C17.6925 20.4793 17.9547 19.1807 18.4791 16.5835L19 14.0039" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            ${escapeHTML(meta.dob)}
          </p>` : ''}
          ${meta.breed != '' ? `<p class="new-pet-card-breed">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.1041 14.0898V14.6035M11.2246 16.4012H12.7655L11.9951 17.1716L11.2246 16.4012Z" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M4.20879 11.2617C3.92182 12.372 3.77685 13.5142 3.77735 14.6609C3.77735 18.9466 7.45698 21.2806 11.9954 21.2806C16.5338 21.2806 20.2135 18.9466 20.2135 14.6609C20.2083 13.5094 20.0378 12.3647 19.707 11.2617M7.88637 14.0898V14.6034" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M8.40101 8.43552C8.00655 9.51414 7.28849 10.5188 5.99312 11.0037C4.00949 11.7453 2.31965 10.6986 2.23747 9.97641C2.12139 8.95531 3.44655 3.26842 6.3465 2.78561C8.32191 2.45586 10.097 3.65364 10.097 5.08153C11.3963 4.7517 12.7591 4.76657 14.0509 5.12467C14.0509 3.69679 15.9452 2.45586 17.9206 2.78561C20.8205 3.26842 22.1457 8.95531 22.0296 9.97641C21.9474 10.6986 20.2576 11.7453 18.274 11.0037C16.9786 10.5188 16.3684 9.51414 15.9739 8.43552" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            ${escapeHTML(meta.breed)}
          </p>` : ''}
        </div>
      </a>
    </swiper-slide>`
  }

  scrollStepIntoView(step) {
    let container = this.querySelector('.new-quiz-type-wrapper');

    container.scrollTo({
      left: step.offsetLeft - container.clientWidth / 2 + step.clientWidth / 2,
      behavior: 'smooth'
    });
  }

  async fetchPageData(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`status ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error(`Quiz pets fetch failed for ${url}:`, err);
      return null;
    }
  }

  async getAllPetsFromUrl(startUrl, petType, forceRefresh = false) {
    if (!forceRefresh) {
      const cached = await this.readCache(petType);
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        return cached.pets;
      }
    }
    const firstData = await this.fetchPageData(startUrl);
    if (!firstData) return [];
    let allPets = firstData.pets;
    let complete = true;
    // Page count is known after page 1, so fetch the rest a few at a time instead of one after another.
    // Shopify names the page param per paginate tag (e.g. page_f28b8882), so copy it from next_page.
    const PARALLEL_PAGES = 6;
    const pageUrls = [];
    if (firstData.next_page) {
      const nextUrl = new URL(firstData.next_page, window.location.origin);
      const pageParam = [...nextUrl.searchParams.keys()].find((key) => /^page(_|$)/.test(key)) || 'page';
      for (let page = 2; page <= (firstData.total_pages || 1); page++) {
        nextUrl.searchParams.set(pageParam, page);
        pageUrls.push(nextUrl.pathname + nextUrl.search);
      }
    }
    for (let i = 0; i < pageUrls.length; i += PARALLEL_PAGES) {
      const batch = pageUrls.slice(i, i + PARALLEL_PAGES).map((url) => this.fetchPageData(url));
      for (const data of await Promise.all(batch)) {
        if (data) allPets = allPets.concat(data.pets);
        else complete = false;
      }
    }
    // A failed page means a partial list; show it but don't cache it for CACHE_TTL_MS
    if (complete) await this.writeCache(petType, allPets);
    return allPets;
  }

  normalizeAnswer(rawValue) {
    if (rawValue === 'Yes') return 'true';
    if (rawValue === 'No') return 'false';
    return rawValue;
  }

  collectAnswersFromDOM() {
    const answers = {};
    document.querySelectorAll('.new-quiz-option-swiper').forEach((container) => {
      const petField = container.dataset.key;
      if (!petField) return;
      const selected = container.querySelector('input[type="radio"]:checked');
      if (!selected) return;
      answers[petField] = this.normalizeAnswer(selected.value);
    });
    return answers;
  }

  filterPets(pets, answers) {
    return pets.filter((pet) => {
      return Object.entries(answers).every(([petField, expectedValue]) => {
        const petValue = pet[petField];
        if (petValue === undefined || petValue === null) return true;
        return String(petValue).toLowerCase() === String(expectedValue).toLowerCase();
      });
    });
  }

  getMatchingPets(pets) {
    const answers = this.collectAnswersFromDOM();
    const matched = this.filterPets(pets, answers);
    return matched;
  }

  openCacheDb() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(CACHE_DB_NAME, CACHE_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
          db.createObjectStore(CACHE_STORE_NAME, { keyPath: 'petType' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
 
  async readCache(petType) {
    const db = await this.openCacheDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CACHE_STORE_NAME, 'readonly');
      const req = tx.objectStore(CACHE_STORE_NAME).get(petType);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }
 
  async writeCache(petType, pets) {
    const db = await this.openCacheDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CACHE_STORE_NAME, 'readwrite');
      tx.objectStore(CACHE_STORE_NAME).put({ petType, pets, fetchedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

}
customElements.define('new-quiz', NewQuiz);

class PetPartnerSlider extends HTMLElement {
  constructor() {
    super();
    this.swiperEl = this.querySelector('.pet-partner-swiper');
  }

  connectedCallback() {
    this.initSwiper();
  }

 initSwiper() {
  if (!this.swiperEl) return;

  Object.assign(this.swiperEl, {
    slidesPerView: 'auto',
    spaceBetween: 8,
    grabCursor: true
  });

  this.swiperEl.initialize();
}


}

customElements.define('pet-partner-slider', PetPartnerSlider);

class CartRelatedProductsMobile extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.loadRecommendations(this.dataset.productId);
  }
  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector(
          'cart-related-products-mobile',
        );
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define(
  'cart-related-products-mobile',
  CartRelatedProductsMobile,
);

class NewPdpPets extends HTMLElement {
  constructor(){
    super();
    this.close = this.querySelector('.new-pdp-pets-location-close');
    this.overlay = this.querySelector('.new-pdp-pets-location-popup');
    this.popup = this.querySelector('.new-pdp-pets-location-popup-inner');
    this.btn = this.querySelector('.new-pdp-pets-button');
    this.btn_gen = this.querySelector('.new-pdp-pets-gen-button');

    this.hold_close = this.querySelector('.new-pdp-pets-close');
    this.hold_popup_overlay = this.querySelector('.new-pdp-pets-popup');
    this.hold_popup = this.querySelector('.new-pdp-pets-popup-inner');
    this.hold_btn = this.querySelector('.new-pdp-pets-hold-button');

    this.hold_time = this.querySelector('.new-pdp-pets-hold-time');

    this.init();
  }

  init(){

    if(this.close){
      this.close.addEventListener('click', (e) => {
        this.overlay.classList.remove('active');
      })
    }

    if(this.overlay){
      this.overlay.addEventListener('click', (e) => {
        this.overlay.classList.remove('active');
      })
    }

    if(this.popup){
      this.popup.addEventListener('click', (e) => {
        e.stopPropagation();
      })
    }

    if(this.btn){
      this.btn.addEventListener('click', (e) => {
        this.overlay.classList.add('active');
      })
    }

    if(this.btn_gen){
      this.btn_gen.addEventListener('click', (e) => {
        this.overlay.classList.add('active');
      })
    }

    if(this.hold_close){
      this.hold_close.addEventListener('click', (e) => {
        this.hold_popup_overlay.classList.remove('active');
      })
    }

    if(this.hold_popup_overlay){
      this.hold_popup_overlay.addEventListener('click', (e) => {
        this.hold_popup_overlay.classList.remove('active');
      })
    }

    if(this.hold_popup){
      this.hold_popup.addEventListener('click', (e) => {
        e.stopPropagation();
      })
    }

    if(this.hold_btn){
      this.hold_btn.addEventListener('click', (e) => {
        this.hold_popup_overlay.classList.add('active');
      })
    }

    if(this.hold_time){
      let utcStr = this.hold_time.getAttribute('data-utc-time');
      let localDate = new Date(utcStr);
      this.hold_time.textContent = localDate.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).replace(' at', ' ').replace(' pm', ' PM').replace(' am', ' AM');
    }
  }
}
customElements.define('new-pdp-pets', NewPdpPets);


class ScrollToTop extends HTMLElement {
  constructor() {
    super();
    this.threshold = window.innerHeight;
    this.button = this.querySelector('.new-scroll-to-top-btn');
    this.handleScroll = this.handleScroll.bind(this);
    this.scrollTop = this.scrollTop.bind(this);
    this.init();
  }

  init() {
    if(this.button){
    this.button.addEventListener('click', this.scrollTop);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
    }
  }

  handleScroll() {
    if (window.scrollY >= this.threshold) {
      this.button.classList.add('active');
    } else {
      this.button.classList.remove('active');
    }
  }

  scrollTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
}
customElements.define('scroll-to-top', ScrollToTop);


class NewHeroBanner extends HTMLElement {
  constructor(){
    super();
    this.buttons = this.querySelectorAll('.new-hero-banner-video-btn');
    this.init();
  }

  init(){
    if(this.buttons){
    this.buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        let video = e.currentTarget.closest('.new-hero-banner-slide').querySelector('.new-hero-banner-video');
        if(e.currentTarget.classList.contains('active')){
          video.play();
          e.currentTarget.classList.remove('active');
        }
        else{
          video.pause();
          e.currentTarget.classList.add('active');
        }
      })
    })
    }
  }
}
customElements.define('new-hero-banner', NewHeroBanner);


async function removeVariantFromCart(vid) {
  let cartRes = await fetch('/cart.js');
  let cart = await cartRes.json();
  let itemIndex = cart.items.findIndex(item => item.id == vid);
  if (itemIndex === -1) {
    return;
  }
  let line = itemIndex + 1;
  let cartItemsEl = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  if (cartItemsEl && typeof cartItemsEl.updateQuantity === 'function') {
    let fakeEvent = { currentTarget: document.createElement('div') };
    cartItemsEl.updateQuantity(line, 0, fakeEvent, undefined, vid);
  } else {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    });
  }
}

function removeHoldFromCart(){
  const vid = document.querySelector('body').getAttribute('data-hold-id');
  if(vid != undefined && vid != null){
    removeVariantFromCart(vid);
  }
}

document.addEventListener('DOMContentLoaded', removeHoldFromCart);

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    removeHoldFromCart();
  }
});

// Cards rendered with defer_media: true (closed cart drawer, search dropdown) get their image after the page has loaded
window.addEventListener('load', () => {
  (window.requestIdleCallback || setTimeout)(() => {
    for (const img of document.querySelectorAll('img[data-deferred-src], img[data-deferred-srcset]')) {
      if (img.dataset.deferredSrcset) img.srcset = img.dataset.deferredSrcset;
      if (img.dataset.deferredSrc) img.src = img.dataset.deferredSrc;
      img.removeAttribute('data-deferred-srcset');
      img.removeAttribute('data-deferred-src');
    }
  });
});

// Pet cards rendered with lazy: true (snippets/new-pet-card.liquid) get their photo shortly before they scroll into view
const lazyPetImageObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.style.setProperty('--pet-image', entry.target.dataset.petImage);
        entry.target.removeAttribute('data-pet-image');
        observer.unobserve(entry.target);
      }
    }, { rootMargin: '800px 0px' })
  : null;

function observeLazyPetImages(root = document) {
  for (const card of root.querySelectorAll('[data-pet-image]')) {
    if (lazyPetImageObserver) lazyPetImageObserver.observe(card);
    else card.style.setProperty('--pet-image', card.dataset.petImage);
  }
}

document.addEventListener('DOMContentLoaded', () => observeLazyPetImages());

// Pet search for the header and search page: every text field Surge can match (the yes/no flags can't be text-searched),
// kept to the theme's pet types so NCR item metaobjects (ncr_live_animal_ncr) never show as pet cards
function surgePetSearch(q, petTypes) {
  const types = (petTypes || '').split(',').filter(Boolean);
  return {
    collection: 'meta_objects',
    q,
    query_by: 'values.name,values.breed,values.feature,values.key_traits,values.locations,values.gender,values.time_available,values.dob,values.description,values.ncr,values.pet_type,values.pet_subtype,values.badge,values.quiz_pet_type,values.exotic',
    filter_by: types.length ? `type:[${types.join(',')}]` : 'type:!=ncr_live_animal_ncr',
  };
}

// Pets from the prefs app (/apps/prefs/animals), used when the Surge API theme setting is off.
// Returned as Surge-style hits so renderSearchPetCard can render them; [] on any error so products still show
async function prefsPetSearch(q) {
  try {
    const response = await fetch(`/apps/prefs/animals?${new URLSearchParams({ q, limit: 10 })}`);
    if (!response.ok) throw new Error(`Prefs ${response.status}`);
    const { items = [] } = await response.json();
    return items
      .filter((pet) => pet.metaobjectType && pet.handle)
      .map((pet) => ({ document: { type: pet.metaobjectType, handle: pet.handle, imageUrls: pet.images || [], values: { name: pet.name, breed: pet.breed, dob: pet.dob } } }));
  } catch (error) {
    console.error('Prefs pet search error:', error);
    return [];
  }
}

// Surge stores metaobject values JSON-encoded ("\"Akita\"", "null"); plain strings pass through.
// Empty, whitespace and "null" values come back as '' so callers can skip that line.
function surgeValue(raw) {
  if (raw == null) return '';
  let text = String(raw);
  try {
    const parsed = JSON.parse(text);
    text = parsed == null ? '' : String(parsed);
  } catch (error) {}
  text = text.trim();
  return text.toLowerCase() === 'null' ? '' : text;
}

function escapeHTML(text) {
  const div = document.createElement('div');
  div.textContent = text ?? '';
  return div.innerHTML.replaceAll('"', '&quot;');
}

// Pet and product cards shared by the header search (NewSearch) and the search page (NewSearchPage)
function renderSearchPetCard(meta) {
  return `
      <a href="/pages/${meta.type.replaceAll("_", "-")}/${meta.handle}" class="new-pet-card zoom-bg" style="--pet-image:
        linear-gradient(180deg, rgba(0, 0, 0, 0) 74.22%, rgba(33, 33, 33, 0.6) 100%),
        linear-gradient(0.24deg, rgba(0, 0, 0, 0.1) 0.31%, rgba(0, 0, 0, 0) 99.9%),
        url('${meta.imageUrls.length > 0 ? meta.imageUrls[0] + '&width=500' : window.petCardPlaceholder}');
      "> 
      <h2 class="new-pet-card-name">${escapeHTML(surgeValue(meta.values.name))}</h2>
        <div class="new-pet-card-desc">
          ${surgeValue(meta.values.dob) ? `<p class="new-pet-card-dob">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5 4.50391C13.5 5.33234 12.8284 6.00391 12 6.00391C11.1716 6.00391 10.5 5.33234 10.5 4.50391C10.5 3.67548 12 2.00391 12 2.00391C12 2.00391 13.5 3.67548 13.5 4.50391Z" stroke="currentColor" stroke-width="1.212" stroke-linejoin="round"></path>
              <path d="M12.002 6V9" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M17.6667 14C19.2315 14 20.5 12.8807 20.5 11.5C20.5 10.1193 19.2315 9 17.6667 9H6.33333C4.76853 9 3.5 10.1193 3.5 11.5C3.5 12.8807 4.76853 14 6.33333 14C7.70408 14 8.90415 13.1411 9.16667 12C9.42919 13.1411 10.6293 14 12 14C13.3707 14 14.5708 13.1411 14.8333 12C15.0959 13.1411 16.2959 14 17.6667 14Z" stroke="currentColor" stroke-width="1.212" stroke-linejoin="round"></path>
              <path d="M5 14.0039L5.52089 16.5835C6.04532 19.1807 6.30754 20.4793 7.19608 21.2416C8.08462 22.0039 9.33608 22.0039 11.839 22.0039H12.161C14.6639 22.0039 15.9154 22.0039 16.8039 21.2416C17.6925 20.4793 17.9547 19.1807 18.4791 16.5835L19 14.0039" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            ${escapeHTML(surgeValue(meta.values.dob))}
          </p>` : ''}
          ${surgeValue(meta.values.breed) ? `<p class="new-pet-card-breed">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.1041 14.0898V14.6035M11.2246 16.4012H12.7655L11.9951 17.1716L11.2246 16.4012Z" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M4.20879 11.2617C3.92182 12.372 3.77685 13.5142 3.77735 14.6609C3.77735 18.9466 7.45698 21.2806 11.9954 21.2806C16.5338 21.2806 20.2135 18.9466 20.2135 14.6609C20.2083 13.5094 20.0378 12.3647 19.707 11.2617M7.88637 14.0898V14.6034" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M8.40101 8.43552C8.00655 9.51414 7.28849 10.5188 5.99312 11.0037C4.00949 11.7453 2.31965 10.6986 2.23747 9.97641C2.12139 8.95531 3.44655 3.26842 6.3465 2.78561C8.32191 2.45586 10.097 3.65364 10.097 5.08153C11.3963 4.7517 12.7591 4.76657 14.0509 5.12467C14.0509 3.69679 15.9452 2.45586 17.9206 2.78561C20.8205 3.26842 22.1457 8.95531 22.0296 9.97641C21.9474 10.6986 20.2576 11.7453 18.274 11.0037C16.9786 10.5188 16.3684 9.51414 15.9739 8.43552" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            ${escapeHTML(surgeValue(meta.values.breed))}
          </p>` : ''}
        </div>
      </a>
    `;
}

function renderSearchProductCard(product, currency) {
  return `
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard card--media" style="--ratio-percent: 125.0%;">
            <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: 125.0%;">
              <a href="/products/${product.handle}" class="card__media" aria-label="${escapeHTML(product.title)}">
                ${renderFpbBadge(product)}${renderSearchProductTags(product.tags)}
                <div class="media media--transparent media--hover-effect">
                  ${product.images.length > 0 ? `<img src="${product.images[0]}&width=533" alt="${escapeHTML(product.title)}">` : window.productCardPlaceholder}
                </div>
              </a>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="/products/${product.handle}" class="full-unstyled-link">${escapeHTML(product.title)}</a>
                  </h3>
                </div>
                <div class="card__badge"></div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <div class="new-card-info">
                    <a href="/products/${product.handle}" class="full-unstyled-link">${escapeHTML(product.title)}</a>
                    <div class="price ${product.compare_at_price > product.price ? 'price--on-sale' : ''}">
                      <div class="price__container">
                        <div class="price__regular">
                          <span class="visually-hidden visually-hidden--inline">Regular price</span>
                          <span class="price-item price-item--regular">
                            ${currency}${product.price > 0 ? `${product.price}` : ''}
                          </span>
                        </div>
                        <div class="price__sale">
                          <span class="visually-hidden visually-hidden--inline">Sale price</span>
                          <span class="price-item price-item--sale price-item--last">
                            ${currency}${product.price > 0 ? `${product.price}` : ''}
                          </span>
                          <span class="visually-hidden visually-hidden--inline">Regular price</span>
                          <span>
                            <s class="price-item price-item--regular">
                              ${currency}${product.compare_at_price > product.price ? `${product.compare_at_price}` : ''}
                            </s>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </h3>
                <div class="card-information">
                  <span class="caption-large light"></span>
                  ${product.metafields && product.metafields['custom.subtitle'] ? `<p class="new-card-product-subtitle">${escapeHTML(product.metafields['custom.subtitle'])}</p>` : ''}
                </div>
              </div>
              <div class="quick-add no-js-hidden">
                ${product.total_variant_count && product.total_variant_count > 1 ? `<modal-opener data-modal="#QuickAddSearch-${product.id.split("/")[product.id.split("/").length - 1]}">
                  <button type="submit" name="add" class="quick-add__submit button button--full-width button--secondary" aria-haspopup="dialog" data-product-url="/products/${product.handle}"> Choose options <div class="loading__spinner hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                        <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                      </svg>
                    </div>
                  </button>
                </modal-opener>
                <quick-add-modal id="QuickAddSearch-${product.id.split("/")[product.id.split("/").length - 1]}" class="quick-add-modal">
                  <div role="dialog" aria-label="Choose options for ${escapeHTML(product.title)}" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">
                    <button id="ModalClose-${product.id.split("/")[product.id.split("/").length - 1]}" type="button" class="quick-add-modal__toggle" aria-label="Close">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17">
                        <path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path>
                      </svg>
                    </button>
                    <div id="QuickAddInfo-${product.id.split("/")[product.id.split("/").length - 1]}" class="quick-add-modal__content-info"></div>
                  </div>
                </quick-add-modal>`
                : `<product-form>
                  <form method="post" action="/cart/add" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
                    <input type="hidden" name="form_type" value="product">
                    <input type="hidden" name="utf8" value="✓">
                    <input type="hidden" name="id" value="${product.first_variant_id && product.first_variant_id.split("/")[product.first_variant_id.split("/").length - 1]}" class="product-variant-id">
                    <button type="submit" name="add" class="quick-add__submit button button--full-width button--secondary" aria-live="polite" data-sold-out-message="true">
                      <span>Add to cart </span>
                      <span class="sold-out-message hidden"> Sold out </span>
                      <div class="loading__spinner hidden">
                        <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                          <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                        </svg>
                      </div>
                    </button>
                    <input type="hidden" name="product-id" value="${product.id.split("/")[product.id.split("/").length - 1]}">
                  </form>
                </product-form>`}
              </div>
              <div class="card__badge"></div>
            </div>
          </div>
        </div>
      `;
}

function renderSearchProductTags(tags) {
  const normalizedTags = [tags ?? []].flat().map((tag) => String(tag).trim().toUpperCase());
  if (normalizedTags.includes('LOW STOCK')) {
    return `<span class="new-card-badge tag_popular color-scheme-199fdacd-f14b-423c-85cb-365f7b8a0e3a">Low Stock</span>`;
  }
  if (normalizedTags.includes('POPULAR')) {
    return `<span class="new-card-badge tag_popular color-scheme-199fdacd-f14b-423c-85cb-365f7b8a0e3a">Popular</span>`;
  }
  return '';
}

function rankSearchResults(query, metaHits = [], productHits = []) {
  const normalize = (text) => String(text ?? '').toLowerCase().replace(/<[^>]*>/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
  const parseValue = (value) => {
    try { return JSON.parse(value); } catch (e) { return value; }
  };

  const phrase = normalize(query);
  const words = phrase.split(' ').filter((word) => word.length > 1);

  const startsWord = (text, term) => ` ${text}`.includes(` ${term}`);

  const getRank = (text) => {
    if (phrase && startsWord(text, phrase)) return 0;
    const wordIndex = words.findIndex((word) => startsWord(text, word));
    return wordIndex === -1 ? words.length + 1 : wordIndex + 1;
  };

  const editDistance = (a, b, max = 3) => {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      const curr = [i];
      for (let j = 1; j <= b.length; j++) {
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      }
      prev = curr;
    }
    return Math.min(prev[b.length], max + 1);
  };

  const getFuzzyScore = (text) => {
    const tokens = [...new Set(text.split(' '))];
    return words.reduce((total, word) => total + Math.min(...tokens.map((token) => editDistance(word, token))), 0);
  };

  const items = [
    ...metaHits.map(({ document }) => ({
      type: 'meta',
      document,
      text: normalize(['name', 'breed', 'feature'].map((key) => [parseValue(document.values?.[key])].flat().join(' ')).join(' ')),
    })),
    ...productHits.map(({ document }) => ({
      type: 'product',
      document,
      text: normalize([document.title, document.vendor, document.product_type, [document.tags].flat().join(' '), document.description].join(' ')),
    })),
  ];

  return items
    .map((item, index) => {
      const rank = getRank(item.text);
      const fuzzy = rank > words.length ? getFuzzyScore(item.text) : 0;
      return { ...item, index, rank, fuzzy };
    })
    .sort((a, b) => a.rank - b.rank || a.fuzzy - b.fuzzy || a.index - b.index);
}

const fpbTextCache = new Map();

function renderFpbBadge(product) {
  if (!product?.handle) return '';
  return `<span class="new-card-gift-badge hidden" data-fpb-handle="${product.handle}">
    <svg width="25" height="27" viewBox="0 0 25 27" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.4602 24.979V13.979M12.4602 6.31234H7.56645C4.21934 6.31234 4.01393 0.979004 7.56645 0.979004C11.3727 0.979004 12.4602 6.31234 12.4602 6.31234ZM12.4602 6.31234H17.354C20.8534 6.31234 20.8534 0.979004 17.354 0.979004C13.5477 0.979004 12.4602 6.31234 12.4602 6.31234Z" stroke="#AF272F" stroke-width="1.95833" stroke-linecap="round" stroke-linejoin="round"></path>
      <path d="M22.7007 13.7647V23.2647C22.7007 23.9846 22.4315 24.675 21.9522 25.184C21.473 25.693 20.823 25.979 20.1452 25.979H12.4785H4.81185C4.13407 25.979 3.48406 25.693 3.0048 25.184C2.52554 24.675 2.25629 23.9846 2.25629 23.2647V13.7647M23.9785 13.7647V9.69329C23.9785 8.97342 23.7093 8.28303 23.23 7.774C22.7507 7.26497 22.1007 6.979 21.423 6.979H3.53407C2.8563 6.979 2.20628 7.26497 1.72702 7.774C1.24776 8.28303 0.978516 8.97342 0.978516 9.69329V13.7647H12.4785H23.9785Z" stroke="#AF272F" stroke-width="1.95833" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
    <em></em>
  </span>`;
}

// Fills the placeholders from renderFpbBadge() inside `container`; badges stay
// hidden when the product has no fpb value. The same response carries the card's
// member badge / member price HTML (snippets/card-member-price.liquid)
function hydrateFpbBadges(container) {
  container?.querySelectorAll('[data-fpb-handle]:not([data-fpb-loaded])').forEach((badge) => {
    const handle = badge.dataset.fpbHandle;
    badge.setAttribute('data-fpb-loaded', '');

    if (!fpbTextCache.has(handle)) {
      fpbTextCache.set(
        handle,
        fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}?view=fpb`)
          .then((response) => (response.ok ? response.json() : ''))
          .catch(() => '')
      );
    }

    fpbTextCache.get(handle).then((data) => {
      if (!data) return;
      if (data.fpb) {
        badge.querySelector('em').textContent = data.fpb;
        badge.classList.remove('hidden');
      }

      const row = badge.closest('.card-wrapper')?.querySelector('.new-card-info');
      if (!row || !(data.member_badge || data.member_price)) return;
      const price = row.querySelector(':scope > .price');
      if (data.member_price && price) price.outerHTML = data.member_price;
      if (data.member_badge) {
        row.setAttribute('data-member-badge', '');
        row.closest('h3')?.insertAdjacentHTML('afterend', data.member_badge);
      }
      window.PlpCardLayout?.scheduleUpdate?.();
    });
  });
}

class NewSearch extends HTMLElement {
  constructor() {
    super();

    this.header = this.closest(".section-header");
    this.body = this.closest("body");
    this.overlay = this.querySelector(".new-header-search-overlay");
    this.searchWrapper = this.querySelector(".new-header-search-wrapper");
    this.searchResultWrapper = this.querySelector(".new-header-search-right");
    this.inputs = ["#site-search", ".new-header-search-mobile-input"];
    this.buttons = [".new-mobile-search-toggle", ".new-header-search-mobile-close-svg"];
    this.currency = this.getAttribute("data-shop-currency");
    this.resultSwiper = this.querySelector(".new-header-search-right-rec-products--results swiper-container");
    this.resultQueryEmpty = this.querySelector(".new-header-search-right-rec-products:not(.new-header-search-right-rec-products--results) .new-header-search-right-result-heading span");
    this.resultQuery = this.querySelector(".new-header-search-right-rec-products--results .new-header-search-right-result-heading span");
    this.resultLink = this.querySelector(".new-header-search-right-rec-products--results .new-header-search-right-result-link");
    this.link = this.querySelector(".new-header-search-right-rec-products:not(.new-header-search-right-rec-products--results) .new-header-search-right-result-link");
    this.surgeAPI = this.getAttribute("data-surge-api");
    this.popularWrapper = this.querySelector(".new-header-search-left-content-ps");

    this.init();
  }

  init() {

    if(this.surgeAPI == "true"){
      this.popularSearchApi();
    }

    this.addEventListener("click", (e) => {
      let popularItem = e.target.closest(".new-header-search-left-item--popular-search");
      if (!popularItem) return;
      let item = popularItem.innerText.trim();
      this.inputs.forEach(input => {
        let inputEl = this.querySelector(input);
        if (!inputEl) return;
        inputEl.value = item;
        inputEl.dispatchEvent(new Event("input", { bubbles: true }));
      });
    });
    
    this.buttons.forEach(button => {
      let buttonEl = this.querySelector(button);
      if (!buttonEl) return;
      buttonEl.addEventListener("click", (e) => {
        e.stopPropagation();
        const wasActive = this.searchWrapper?.classList.contains("active");
        if(this.searchWrapper){
          this.searchWrapper.classList.toggle("active");
        }
        this.body.classList.toggle("overflow-hidden-megamenu");
        this.header.classList.toggle("search-open");

        if (wasActive) {
          setTimeout(() => this.resetSearch(), 0);
        }
      })
    })

    this.inputs.forEach(input => {
      let inputEl = this.querySelector(input);
      if (!inputEl) return;
      inputEl.addEventListener("input", this.debounce(() => {
        if(inputEl.value.length > 0){
          if(this.surgeAPI == "true"){
            this.executeUnifiedStorefrontSearch(inputEl.value);
          }
          else{
            this.search(inputEl.value);
          }
          if(this.searchResultWrapper){
            this.searchResultWrapper.classList.add("searching");
            this.searchResultWrapper.classList.remove("searched");
            this.searchResultWrapper.classList.remove("not-found");
          }
        }
        else{
          if(this.searchResultWrapper){
            this.searchResultWrapper.classList.remove("searching");
            this.searchResultWrapper.classList.remove("searched");
            this.searchResultWrapper.classList.remove("not-found");
          }
        }
      }, 300));
    })
    
    window.PlpCardLayout?.scheduleUpdate?.(this);
    this.initTitleTooltips();
  }

  initTitleTooltips() {
    const titleSelector = ".new-header-search-right-rec-products-item .new-card-info > a";

    this.titleTooltip = document.createElement("div");
    this.titleTooltip.className = "new-search-title-tooltip";
    this.titleTooltip.setAttribute("role", "tooltip");
    document.body.appendChild(this.titleTooltip);

    const isTruncated = (title) => {
      const truncated = title.scrollHeight - title.clientHeight > 1;
      title.classList.toggle("is-truncated", truncated);
      return truncated;
    };

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => isTruncated(entry.target));
    });

    let observeFrame = null;
    const observeTitles = () => {
      if (observeFrame) return;
      observeFrame = requestAnimationFrame(() => {
        observeFrame = null;
        resizeObserver.disconnect();
        this.querySelectorAll(titleSelector).forEach((title) => resizeObserver.observe(title));
      });
    };
    observeTitles();
    if (this.searchResultWrapper) {
      new MutationObserver(observeTitles).observe(this.searchResultWrapper, { childList: true, subtree: true });
    }

    let activeTitle = null;
    const show = (title) => {
      if (title === activeTitle) return;
      if (!isTruncated(title)) return this.hideTitleTooltip();
      activeTitle = title;
      this.titleTooltip.textContent = title.textContent.trim();

      // Keep the tooltip inside the product card it belongs to.
      const gap = 8;
      const card = title.closest(".new-header-search-right-rec-products-item") || title;
      const cardRect = card.getBoundingClientRect();
      this.titleTooltip.style.maxWidth = `${Math.min(cardRect.width, window.innerWidth - gap * 2)}px`;
      this.titleTooltip.classList.add("is-visible");

      const titleRect = title.getBoundingClientRect();
      const tipRect = this.titleTooltip.getBoundingClientRect();
      const left = Math.max(
        Math.min(titleRect.left, cardRect.right - tipRect.width),
        cardRect.left
      );
      let top = titleRect.top - tipRect.height - gap;
      if (top < gap) top = titleRect.bottom + gap;
      this.titleTooltip.style.left = `${left}px`;
      this.titleTooltip.style.top = `${top}px`;
    };

    this.hideTitleTooltip = () => {
      activeTitle = null;
      this.titleTooltip.classList.remove("is-visible");
    };

    this.addEventListener("mouseover", (e) => {
      const title = e.target.closest(titleSelector);
      if (title) show(title);
    });
    this.addEventListener("mouseout", (e) => {
      if (activeTitle && !activeTitle.contains(e.relatedTarget)) this.hideTitleTooltip();
    });
    this.addEventListener("focusin", (e) => {
      const title = e.target.closest(titleSelector);
      if (title) show(title);
    });
    this.addEventListener("focusout", () => this.hideTitleTooltip());
    window.addEventListener("scroll", () => this.hideTitleTooltip(), { capture: true, passive: true });
    window.addEventListener("resize", () => this.hideTitleTooltip());
  }

  search(val) {
    if(this.resultQuery){
      this.resultQuery.innerText = val;
    }
    if(this.resultQueryEmpty){
      this.resultQueryEmpty.innerText = val;
    }
    const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(val)}&section_id=new-predictive-search`;
    // Shopify's predictive search has no pets, so they come from the prefs app and go before the products
    const pets = prefsPetSearch(val);
    fetch(url)
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then(async text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = html.querySelector('#shopify-section-new-predictive-search')?.innerHTML || '';
        const petSlides = (await pets).map(({ document }) => this.renderMetaCard(document)).join('');
        const hasResults = petSlides || (resultsMarkup && html.querySelectorAll("swiper-slide").length);
        if(hasResults){
          if(this.searchResultWrapper){
            this.searchResultWrapper.classList.remove("searching");
            this.searchResultWrapper.classList.add("searched");
          }
          if(this.resultSwiper){
            this.resultSwiper.innerHTML = petSlides + resultsMarkup;
          }
          window.PlpCardLayout?.scheduleUpdate?.(this);
          if(this.resultLink){
            this.resultLink.setAttribute("href", `/search?q=${encodeURIComponent(val)}`);
          }
        }
        else{
          if (this.searchResultWrapper) {
            this.searchResultWrapper.classList.add('not-found');
            this.searchResultWrapper.classList.remove('searching');
            this.searchResultWrapper.classList.remove('searched');
          }
        }
      })
      .catch(error => {
        console.error('New predictive search error:', error);
        if (this.searchResultWrapper) {
          this.searchResultWrapper.classList.add('not-found');
          this.searchResultWrapper.classList.remove('searching');
          this.searchResultWrapper.classList.remove('searched');
        }
      });
  }

  popularSearchApi = async () => {

    const endpointUrl = "/apps/surge/multi_search";
    
    const combinedPayload = {
      "searches": [
        {
          "collection": `popular_search`,
          "q": "*",
          "query_by": "query"
        }
      ],
      "source": "index",
      "search_context": "instant_search"
    };

    try {
      const response = await fetch(endpointUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(combinedPayload)
      });
      const operationalMatrix = await response.json();
      const [popularLookups] = operationalMatrix.results || [];


      let popularSearches = '';
      if(popularLookups.hits.length > 0){
        for(let item of popularLookups.hits){
          if(item.document.query.length > 2){
            popularSearches += this.renderPopularSearches(item.document.query);
          }
        }
      }
      if(popularSearches.length > 0){
        if(this.popularWrapper){
          this.popularWrapper.innerHTML = popularSearches;
        }
      }
      
    } catch (err) {
      console.error("Unified network pipeline error:", err);
      if (this.searchResultWrapper) {
        this.searchResultWrapper.classList.add('not-found');
        this.searchResultWrapper.classList.remove('searching');
        this.searchResultWrapper.classList.remove('searched');
      }
    }
  }

  executeUnifiedStorefrontSearch = async (val) => {
    if(this.resultQuery){
      this.resultQuery.innerText = val;
    }
    if(this.resultQueryEmpty){
      this.resultQueryEmpty.innerText = val;
    }
    const endpointUrl = "/apps/surge/multi_search";
    
    const combinedPayload = {
      "searches": [
        {
          "collection": `products`,
          "q": val,
          "query_by": "title,description,vendor,product_type,tags"
        },
        surgePetSearch(val, this.dataset.petTypes)
      ],
      "source": "index",
      "search_context": "instant_search"
    };

    try {
      const response = await fetch(endpointUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(combinedPayload)
      });
      const operationalMatrix = await response.json();
      const [productLookups, metaLookups] = operationalMatrix.results || [];


      if(productLookups.hits.length > 0 || metaLookups.hits.length > 0){
        let cards = ``;
        for(let item of rankSearchResults(val, metaLookups.hits, productLookups.hits)){
          cards += item.type === 'meta' ? this.renderMetaCard(item.document) : this.renderProductCard(item.document);
        }
        if(this.searchResultWrapper){
          this.searchResultWrapper.classList.remove("searching");
          this.searchResultWrapper.classList.add("searched");
        }
        if(this.resultSwiper){
          this.resultSwiper.innerHTML = cards;
          hydrateFpbBadges(this.resultSwiper);
        }
        window.PlpCardLayout?.scheduleUpdate?.(this);
        if(this.resultLink){
          this.resultLink.setAttribute("href", `/search?q=${encodeURIComponent(val)}`);
        }
      }
      else{
        if(this.searchResultWrapper){
          this.searchResultWrapper.classList.remove("searching");
          this.searchResultWrapper.classList.remove("searched");
          this.searchResultWrapper.classList.add("not-found");
        }
        if(this.link){
          this.link.setAttribute("href", `/collections/all`);
        }
      }
      
    } catch (err) {
      console.error("Unified network pipeline error:", err);
      if (this.searchResultWrapper) {
        this.searchResultWrapper.classList.add('not-found');
        this.searchResultWrapper.classList.remove('searching');
        this.searchResultWrapper.classList.remove('searched');
      }
    }
  }

  // Clears both search inputs and reverts the results panel back to its
  // default (pre-search) state. Called whenever the dropdown is closed so a
  // stale query/results never lingers into the next time it's opened.
  resetSearch() {
    // If the dropdown was re-opened before this deferred reset ran, leave it.
    if (this.searchWrapper?.classList.contains("active")) return;

    this.inputs.forEach((selector) => {
      const inputEl = this.querySelector(selector);
      if (!inputEl) return;
      inputEl.value = "";
      inputEl.blur();
    });

    if (this.searchResultWrapper) {
      this.searchResultWrapper.classList.remove("searching", "searched", "not-found");
    }
    if (this.resultSwiper) this.resultSwiper.innerHTML = "";
    if (this.resultQuery) this.resultQuery.innerText = "";    this.hideTitleTooltip?.();
  }

  renderPopularSearches(item){
    return `<button type="button" class="new-header-search-left-item new-header-search-left-item--popular-search" style="color: #3f444d; border: 1px solid #757575;">${escapeHTML(item)}</button>`;
  }

  renderMetaCard(meta){
    return `<swiper-slide>${renderSearchPetCard(meta)}</swiper-slide>`;
  }


  renderProductCard(product){
    return `<swiper-slide><div class="new-header-search-right-rec-products-item">${renderSearchProductCard(product, this.currency)}</div></swiper-slide>`;
  }




  debounce(fn, delay = 300) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}
customElements.define('new-search', NewSearch);


class NewKlaviyoForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input[type="email"]');
    this.button = this.querySelector('button');
    this.message = this.querySelector('.new-klaviyo-msg');
    this.messageTimeout = null;
    if (!this.form) return;

    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    clearTimeout(this.messageTimeout);
  }

  showMessage(text) {
    if (!this.message) return;

    clearTimeout(this.messageTimeout);
    this.message.textContent = text;
    this.message.classList.add('active');

    this.messageTimeout = setTimeout(() => {
      this.message.classList.remove('active');
    }, 5000);
  }

  async onSubmit(event) {
    event.preventDefault();
    const companyId = this.getAttribute('company-id');
    const listId = this.getAttribute('list-id');
    const email = this.emailInput.value.trim();
    const successMessage = this.getAttribute('success-message') || 'Thanks for subscribing!';
    const errorMessage = this.getAttribute('error-message') || 'Something went wrong, please try again.';
    if (!companyId || !listId) {
      console.error('<new-klaviyo-form> needs company-id and list-id attributes');
      return;
    }
    this.setLoading(true);
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.getAttribute('source') || 'Website Form',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );
      if (response.status === 202) {
        this.form.reset();
        this.showMessage(successMessage);
        this.dispatchEvent(new CustomEvent('klaviyo:success', { bubbles: true, detail: { email } }));
      } else {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.errors?.[0]?.detail || `Request failed (${response.status})`);
      }
    } catch (error) {
      console.error('Klaviyo subscribe error:', error);
      this.showMessage(errorMessage);
      this.dispatchEvent(new CustomEvent('klaviyo:error', { bubbles: true, detail: { error } }));
    } finally {
      this.setLoading(false);
    }
  }
  setLoading(isLoading) {
    if (this.button) {
      this.button.disabled = isLoading;
    }
    if (this.emailInput) {
      this.emailInput.disabled = isLoading;
    }
  }
}
if (!customElements.get('new-klaviyo-form')) {
  customElements.define('new-klaviyo-form', NewKlaviyoForm);
}